/**
 * atomberg-product-variant-picker.js
 * ──────────────────────────────────
 * PDP variant picker behavior. Wires click handlers on color tiles + option
 * chips inside `[data-atomberg-pdp-variant-picker]` roots to the shared
 * AtombergVariantSelector module, then applies the resolved variant to the PDP
 * DOM: active state, color label, URL `?variant=`, Horizon's buy-buttons hidden
 * input, the PDP price block, and emits `atomberg:pdp-variant-change` for any
 * other listeners (gallery, sticky bar, analytics).
 */
(function () {
  /** @type {any} */
  var w = window;
  if (w._atombergPdpVariantPickerInit) return;
  w._atombergPdpVariantPickerInit = true;

  /** @type {any} */
  var V = w.AtombergVariantSelector;
  if (!V) {
    if (w.console && typeof w.console.warn === 'function') {
      w.console.warn(
        '[atomberg-pdp-variant-picker] AtombergVariantSelector not found; ensure atomberg-variant-selector.js loads first.'
      );
    }
    return;
  }

  /**
   * @param {HTMLElement} root
   * @returns {Array<any>}
   */
  function getVariants(root) {
    /** @type {any} */
    var anyRoot = root;
    if (anyRoot._atombergVariants) return anyRoot._atombergVariants;
    var raw = root.getAttribute('data-variants');
    /** @type {Array<any>} */
    var parsed = [];
    if (raw) {
      try {
        parsed = JSON.parse(raw);
        // eslint-disable-next-line no-unused-vars
      } catch (e) {
        parsed = [];
      }
    }
    // data-variants is server-rendered and static for the page's lifetime, so
    // parsing once keeps every later click off the JSON.parse cost.
    anyRoot._atombergVariants = parsed;
    return parsed;
  }

  /**
   * Parse a JSON data-attribute once per element — repeat variant clicks must
   * not re-pay JSON.parse for the same static markup.
   *
   * @param {Element} el
   * @param {string} attr
   * @returns {Record<string, string>}
   */
  function readJsonAttrCached(el, attr) {
    /** @type {any} */
    var anyEl = el;
    var cacheKey = '_atombergJson_' + attr;
    if (anyEl[cacheKey] !== undefined) return anyEl[cacheKey];
    /** @type {Record<string, string>} */
    var parsed = {};
    try {
      parsed = JSON.parse(el.getAttribute(attr) || '{}');
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      parsed = {};
    }
    anyEl[cacheKey] = parsed;
    return parsed;
  }

  /**
   * Toggle `--active` and `aria-pressed` on color tiles + chips inside the
   * picker to reflect the resolved variant.
   *
   * @param {HTMLElement} root
   * @param {Array<string|null>} vOpts
   */
  function setActive(root, vOpts) {
    root.querySelectorAll('[data-option-index]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index') || '0', 10);
      var target = vOpts[idx];
      group.querySelectorAll('.atomberg-color-tile, .atomberg-chip').forEach(function (btn) {
        var isActive = btn.getAttribute('data-option-value') === target;
        if (btn.classList.contains('atomberg-color-tile')) {
          btn.classList.toggle('atomberg-color-tile--active', isActive);
        }
        if (btn.classList.contains('atomberg-chip')) {
          btn.classList.toggle('atomberg-chip--active', isActive);
        }
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    });
  }

  /**
   * Sync the row labels (e.g. "Color: Marble White", "Sweep Size: 1200mm") with
   * the resolved variant's options.
   *
   * @param {HTMLElement} root
   * @param {Array<string|null>} vOpts
   */
  function updateLabels(root, vOpts) {
    var colorGroup = root.querySelector('.atomberg-color-tiles[data-option-index]');
    if (colorGroup) {
      var nameEl = colorGroup.querySelector('[data-atomberg-color-selected-name]');
      var colorIdx = parseInt(colorGroup.getAttribute('data-option-index') || '0', 10);
      if (nameEl) nameEl.textContent = vOpts[colorIdx] || '';
    }

    root.querySelectorAll('.atomberg-product-variant-picker__option').forEach(function (optionBox) {
      var chipsGroup = optionBox.querySelector('.atomberg-chips[data-option-index]');
      if (!chipsGroup) return;
      var idx = parseInt(chipsGroup.getAttribute('data-option-index') || '0', 10);
      var valueEl = optionBox.querySelector('.atomberg-product-variant-picker__option-label-value');
      if (valueEl) valueEl.textContent = vOpts[idx] || '';
    });
  }

  /**
   * Update the PDP price block to reflect the resolved variant.
   *
   * @param {any} variant
   */
  function updatePrice(variant) {
    /** @type {HTMLElement|null} */
    var priceEl = document.querySelector('[data-atomberg-pdp-price]');
    if (!priceEl) return;

    var symbol = priceEl.getAttribute('data-currency-symbol') || '';
    var showDecimals = priceEl.getAttribute('data-show-decimals') === '1';
    var alwaysShowTax = priceEl.getAttribute('data-always-show-tax') === '1';

    var hasCompare = !!variant.compare_at_price && variant.compare_at_price > variant.price;

    var current = priceEl.querySelector('[data-atomberg-pdp-price-current]');
    if (current) {
      current.textContent = V.formatPrice(variant.price, symbol, showDecimals);
    }

    /** @type {HTMLElement|null} */
    var compare = priceEl.querySelector('[data-atomberg-pdp-price-compare]');
    if (compare) {
      var compareValue = compare.querySelector('[data-atomberg-pdp-price-compare-value]');
      if (hasCompare && compareValue) {
        compareValue.textContent = V.formatPrice(variant.compare_at_price, symbol, showDecimals);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }

    /** @type {HTMLElement|null} */
    var discount = priceEl.querySelector('[data-atomberg-pdp-price-discount]');
    if (discount) {
      if (hasCompare) {
        var suffix = discount.getAttribute('data-discount-suffix') || '% off';
        discount.textContent = V.discountPercent(variant) + suffix;
        discount.hidden = false;
      } else {
        discount.hidden = true;
      }
    }

    /** @type {HTMLElement|null} */
    var tax = priceEl.querySelector('[data-atomberg-pdp-price-tax]');
    if (tax) tax.hidden = !(hasCompare || alwaysShowTax);
  }

  /**
   * Swap the PDP title to the resolved variant's `custom.variant_title`,
   * falling back to the product title. The map only contains variants that
   * have the metafield set (rendered by blocks/atomberg-product-title.liquid).
   *
   * @param {any} variant
   */
  function updateTitle(variant) {
    document.querySelectorAll('[data-atomberg-pdp-title]').forEach(function (el) {
      var map = readJsonAttrCached(el, 'data-variant-titles');
      var title = map[String(variant.id)] || el.getAttribute('data-product-title');
      if (title) el.textContent = title;
    });
  }

  /**
   * Swap the PDP description to the resolved variant's plain-text
   * `custom.variant_description`, falling back to the product description
   * (HTML, kept in its own element so it survives the round-trip). Hides the
   * whole block when neither exists. Rendered by
   * blocks/atomberg-product-description.liquid.
   *
   * @param {any} variant
   */
  function updateDescription(variant) {
    document.querySelectorAll('[data-atomberg-pdp-description]').forEach(function (el) {
      var map = readJsonAttrCached(el, 'data-variant-descriptions');
      var text = map[String(variant.id)] || '';
      var variantEl = /** @type {HTMLElement|null} */ (
        el.querySelector('[data-pdp-description-variant]')
      );
      var defaultEl = /** @type {HTMLElement|null} */ (
        el.querySelector('[data-pdp-description-default]')
      );
      if (variantEl) {
        variantEl.textContent = text;
        variantEl.hidden = !text;
      }
      if (defaultEl) defaultEl.hidden = Boolean(text);
      /** @type {HTMLElement} */ (el).hidden = !text && !defaultEl;
    });
  }

  /**
   * Sync Horizon's buy-buttons hidden variant id and fire a `change` event so
   * Horizon's reactive bindings (and any cart drawer hooks) pick up the swap.
   *
   * @param {any} variant
   */
  function updateFormVariantId(variant) {
    var inputs = document.querySelectorAll('input[name="id"][ref="variantId"]');
    inputs.forEach(function (input) {
      /** @type {HTMLInputElement} */ (input).value = String(variant.id);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /**
   * Replace the current URL's `?variant=` parameter without a navigation, so a
   * page refresh keeps the selection and the URL stays shareable.
   *
   * @param {any} variant
   */
  function updateUrl(variant) {
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      /* URL API unavailable — skip */
    }
  }

  /**
   * Immediately sync ATC button state to match the resolved variant. Called
   * synchronously in applyVariant so the button is correct the moment the
   * chip/tile is clicked — before updateGallery's async fetch + Horizon morph
   * can take over. Without this, if the page was opened on an OOS variant URL,
   * the server-rendered "Sold out" / disabled state persists until the fetch
   * resolves (and Horizon's productId guard may skip the update entirely).
   *
   * @param {HTMLElement} root - the picker root (source of translated strings)
   * @param {any} variant
   */
  function updateButtonAvailability(root, variant) {
    var available = !!variant.available;
    var availText = root.getAttribute('data-atc-available-text') || 'Add to Cart';
    var soldText = root.getAttribute('data-atc-soldout-text') || 'Sold out';
    var text = available ? availText : soldText;

    // Main buy button inside <add-to-cart-component>
    document
      .querySelectorAll('add-to-cart-component button[type="submit"]')
      .forEach(function (btn) {
        /** @type {HTMLButtonElement} */ (btn).disabled = !available;
        // Text lives in the innermost <span> inside .add-to-cart-text__content
        var inner = btn.querySelector('.add-to-cart-text__content span span');
        if (inner) inner.textContent = text;
      });

    // Sticky ATC button — text is a direct text node before the quantity span
    document.querySelectorAll('.atomberg-sticky-atc__atc-btn').forEach(function (btn) {
      /** @type {HTMLButtonElement} */ (btn).disabled = !available;
      var textSpan = btn.querySelector('.add-to-cart-text');
      if (!textSpan) return;
      var node = textSpan.firstChild;
      if (node && node.nodeType === 3) node.textContent = text;
    });

    // Buy Now container — toggle synchronously so it matches ATC state immediately
    // rather than waiting for the async gallery fetch to fire Horizon's variant:update.
    document.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"]').forEach(function (el) {
      if (available) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', 'true');
      }
    });
  }

  /**
   * Section keys (templates/product.json JSON keys, not DOM ids — Shopify
   * prefixes each with a per-theme-save template hash) whose content is
   * resolved variant-first server-side (rich-text accordion rows, A+, FAQ).
   * Coupled to those exact keys — renaming a section in product.json means
   * updating this list too.
   * @type {string[]}
   */
  var VARIANT_CONTENT_SECTION_KEYS = [
    'section_4rFMtd', // specs accordion (description/features/performance/specification/warranty/manufacturer)
    'atomberg_aplus_content_7gDpEd', // A+ content
    'atomberg_faq_product', // FAQ
    'atomberg_pdp_seo_content', // SEO content grid (variant-only, no product fallback)
  ];

  /**
   * Re-execute <script> tags inside a fragment that was just inserted via
   * innerHTML — the browser never runs scripts assigned that way, but
   * atomberg-metafield-text's "hide this row when blank" script needs to run
   * so a row that flips blank/populated on variant switch shows correctly.
   * @param {Element} container
   */
  function executeScripts(container) {
    container.querySelectorAll('script').forEach(function (old) {
      var fresh = document.createElement('script');
      for (var i = 0; i < old.attributes.length; i++) {
        var attr = old.attributes[i];
        if (attr) fresh.setAttribute(attr.name, attr.value);
      }
      fresh.textContent = old.textContent;
      old.replaceWith(fresh);
    });
  }

  /**
   * Jobs applied one per animation frame — each swapped section (A+, FAQ,
   * specs, SEO) can be large, and a single task doing all of them blocks the
   * tap that triggered the variant switch from painting (INP). Only these
   * content-only sections are chunked; the gallery/price/sticky-ATC morphs
   * that Horizon's own listeners perform on `variant:update` are left to run
   * at their normal (immediate) time — see updateGallery below.
   * @type {Array<() => void>}
   */
  var swapQueue = [];
  var swapScheduled = false;

  function drainSwapQueue() {
    var job = swapQueue.shift();
    if (job) job();
    if (swapQueue.length) {
      requestAnimationFrame(drainSwapQueue);
    } else {
      swapScheduled = false;
    }
  }

  /** @param {() => void} job */
  function queueSwap(job) {
    swapQueue.push(job);
    if (!swapScheduled) {
      swapScheduled = true;
      requestAnimationFrame(drainSwapQueue);
    }
  }

  /**
   * Swap the specs accordion / A+ / FAQ sections to match the fetched
   * variant's server-rendered HTML — these blocks resolve variant-first with
   * product-level fallback in Liquid, so the fetched page already has the
   * correct content; this just brings it into the live DOM without a second
   * request. Reuses updateGallery's fetch rather than firing its own. Each
   * section swaps on its own animation frame, and a section whose HTML is
   * unchanged (most variants share A+/FAQ/specs) is skipped without touching
   * the DOM at all.
   * @param {Document} html
   */
  function updateVariantSections(html) {
    VARIANT_CONTENT_SECTION_KEYS.forEach(function (key) {
      var selector = '[id$="__' + key + '"]';
      var currentEl = document.querySelector(selector);
      var nextEl = html.querySelector(selector);
      if (!currentEl || !nextEl) return;
      var current = currentEl;
      var next = nextEl;
      if (next.innerHTML === current.innerHTML) return;
      queueSwap(function () {
        current.innerHTML = next.innerHTML;
        executeScripts(current);
      });
    });
  }

  /**
   * Swap the color option's tile/swatch row to match the fetched variant's
   * server-rendered HTML. atomberg-color-tiles / atomberg-color-swatches
   * resolve each value's image/hex variant-first (matching every other
   * currently-selected option) in Liquid, so the fetched page already has
   * the correct visuals — this just brings that into the live DOM without
   * a second request. Necessary because nothing else ever refreshes this
   * row after the initial page load: setActive() only toggles active/
   * unavailable classes on the existing markup, so without this swap the
   * tile images (or swatch hex values) stay frozen showing whichever
   * option combination was selected when the page first rendered, even
   * after later clicks change other options.
   * @param {HTMLElement} root
   * @param {Document} html
   */
  function updateColorRow(root, html) {
    var currentEl = root.querySelector('.atomberg-color-tiles, .atomberg-swatches-wrapper');
    if (!currentEl) return;
    var nextRoot = html.querySelector('[data-atomberg-pdp-variant-picker]');
    var nextEl = nextRoot
      ? nextRoot.querySelector('.atomberg-color-tiles, .atomberg-swatches-wrapper')
      : null;
    if (!nextEl) return;
    var current = currentEl;
    var next = nextEl;
    if (next.innerHTML === current.innerHTML) return;
    queueSwap(function () {
      current.innerHTML = next.innerHTML;
      executeScripts(current);
      // atomberg-color-tiles.js binds drag-to-scroll directly to the
      // .atomberg-color-tiles__row element it finds at page load; the
      // innerHTML swap discards that node, silently losing drag-to-scroll
      // on every option change unless it's re-armed on the fresh one here.
      var w = /** @type {any} */ (window);
      if (w.AtombergDragScroll) {
        current.querySelectorAll('.atomberg-color-tiles__row').forEach(function (row) {
          w.AtombergDragScroll.enable(row, { dragClass: 'atomberg-color-tiles__row--dragging' });
        });
      }
    });
  }

  /** @type {AbortController|null} */
  var galleryFetchController = null;

  /**
   * Update the product media gallery by fetching the product page with the new
   * variant ID and dispatching Horizon's `variant:update` event with the parsed
   * HTML. Horizon's `<media-gallery>`, `product-form`, `product-price`,
   * `product-inventory`, `sticky-add-to-cart` and other native components all
   * listen for this on `.shopify-section` and read `detail.data.html` — they
   * expect the FULL parsed page document (this is exactly the fetch/parse
   * mechanism Horizon's own `variant-picker.js` uses for the main PDP; a
   * Section-Rendering-API fragment was tried here and reverted because
   * several of those listeners read from sections outside the main one and
   * silently no-op on a fragment, most visibly leaving the sticky ATC bar's
   * price and cart state stale).
   *
   * `variant:update` dispatches immediately so those components' own (already
   * lightweight, targeted) morphs run at their normal time; only the heavier
   * A+/FAQ/specs/SEO content-section innerHTML swaps are deferred/chunked via
   * updateVariantSections, since those are what caused the long blocking task.
   *
   * @param {HTMLElement} root
   * @param {any} variant
   */
  function updateGallery(root, variant) {
    var productUrl = root.getAttribute('data-product-url');
    if (!productUrl) return;

    var section = root.closest('.shopify-section');
    if (!section) return;
    var targetSection = section;

    if (galleryFetchController) galleryFetchController.abort();
    galleryFetchController = new AbortController();

    var sep = productUrl.indexOf('?') >= 0 ? '&' : '?';
    var url = productUrl + sep + 'variant=' + encodeURIComponent(String(variant.id));

    fetch(url, { signal: galleryFetchController.signal })
      .then(function (response) {
        return response.text();
      })
      .then(function (responseText) {
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        // Liquid's variant JSON has no top-level product_id, so the old
        // String(variant.product_id || '') dispatched '' — Horizon's
        // product-form and sticky-add-to-cart both drop any variant:update
        // whose data.productId doesn't match their data-product-id, so the
        // sticky bar's price/variant morph and the native form sync silently
        // never ran. Read the id from the picker root instead.
        var productId =
          root.getAttribute('data-product-id') ||
          (variant.featured_image ? String(variant.featured_image.product_id || '') : '');
        targetSection.dispatchEvent(
          new CustomEvent('variant:update', {
            bubbles: true,
            detail: {
              resource: variant,
              sourceId: 'atomberg-pdp-variant-picker',
              data: {
                html: html,
                productId: productId,
                newProduct: null,
              },
            },
          })
        );
        updateVariantSections(html);
        updateColorRow(root, html);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        if (window.console && typeof window.console.warn === 'function') {
          window.console.warn('[atomberg-pdp-variant-picker] gallery update failed', error);
        }
      });
  }

  /**
   * Apply a resolved variant across the PDP and emit the change event.
   *
   * @param {HTMLElement} root
   * @param {any} variant
   * @param {Array<any>} variants
   */
  function applyVariant(root, variant, variants) {
    if (!variant) return;
    var vOpts = [variant.option1 || null, variant.option2 || null, variant.option3 || null];

    setActive(root, vOpts);
    updateLabels(root, vOpts);
    updatePrice(variant);
    updateTitle(variant);
    updateDescription(variant);
    updateButtonAvailability(root, variant);
    updateFormVariantId(variant);
    updateUrl(variant);
    updateGallery(root, variant);
    V.recomputeAvailability(root, variants, vOpts);

    root.dispatchEvent(
      new CustomEvent('atomberg:pdp-variant-change', {
        bubbles: true,
        detail: { variant: variant },
      })
    );
  }

  /**
   * @param {HTMLElement} root
   */
  function initPicker(root) {
    var variants = getVariants(root);
    var opts = V.readSelections(root);
    V.recomputeAvailability(root, variants, opts);
  }

  document.addEventListener('click', function (event) {
    var target = /** @type {HTMLElement} */ (event.target);
    var btn = target.closest('.atomberg-color-tile, .atomberg-chip');
    if (!btn) return;

    var root = /** @type {HTMLElement|null} */ (btn.closest('[data-atomberg-pdp-variant-picker]'));
    if (!root) return;

    var group = /** @type {HTMLElement|null} */ (btn.closest('[data-option-index]'));
    if (!group) return;

    var optIdx = parseInt(group.getAttribute('data-option-index') || '0', 10);
    var optValue = btn.getAttribute('data-option-value');
    if (!optValue) return;

    var variants = getVariants(root);
    var opts = V.readSelections(root);
    var best = V.findBestAvailable(variants, opts, optIdx, optValue);
    if (best) applyVariant(root, best, variants);
  });

  function start() {
    document.querySelectorAll('[data-atomberg-pdp-variant-picker]').forEach(function (el) {
      initPicker(/** @type {HTMLElement} */ (el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
